export interface SellerOptionLike {
  value: string;
  label: string;
}

const SELLER_FIELD_KEYS = new Set(['sellersIncluidos', 'sellersExcecao']);
const UF_RE = /\b(ac|al|ap|am|ba|ce|df|es|go|ma|mt|ms|mg|pa|pb|pr|pe|pi|rj|rn|rs|ro|rr|sc|sp|se|to)\b$/i;
const LEADING_ID_RE = /^\s*(\d+)\s*(?:-|[\u2013\u2014])\s*/;

export function isSellerFieldKey(key: string): boolean {
  return SELLER_FIELD_KEYS.has(key.replace(/__child\d+$/, ''));
}

export function normalizeSellerText(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function stripSellerIdPrefix(value: unknown): string {
  return String(value ?? '').trim().replace(LEADING_ID_RE, '').trim();
}

function getSellerIdFromOption(option: SellerOptionLike): string {
  const value = String(option.value ?? '').trim();
  if (/^\d+$/.test(value)) return value;

  const labelMatch = String(option.label ?? '').match(LEADING_ID_RE);
  return labelMatch?.[1] ?? '';
}

export function getSellerOptionDisplayValue(option: SellerOptionLike): string {
  const labelWithoutId = stripSellerIdPrefix(option.label);
  if (labelWithoutId) return labelWithoutId;

  const valueWithoutId = stripSellerIdPrefix(option.value);
  return valueWithoutId || String(option.value ?? '').trim();
}

export function getSellerOptionAliases(option: SellerOptionLike): string[] {
  const sellerId = getSellerIdFromOption(option);
  const displayValue = getSellerOptionDisplayValue(option);
  const displayWithoutUf = displayValue.replace(UF_RE, '').replace(/\s*-\s*$/, '').trim();

  return Array.from(new Set([
    sellerId,
    option.value,
    option.label,
    stripSellerIdPrefix(option.value),
    stripSellerIdPrefix(option.label),
    displayValue,
    displayWithoutUf,
  ]
    .map(normalizeSellerText)
    .filter((item) => item.length >= 2)));
}

function getSellerOptionMatchScore(input: string, option: SellerOptionLike): number {
  const normalizedInput = normalizeSellerText(input);
  if (!normalizedInput) return 0;

  const aliases = getSellerOptionAliases(option);
  if (aliases.some((alias) => alias === normalizedInput)) return 120;

  const inputTokens = normalizedInput.split(' ').filter((token) => token.length >= 2);
  if (inputTokens.length === 0) return 0;

  if (aliases.some((alias) => alias.length >= 4 && normalizedInput.includes(alias) && alias.split(' ').length >= 2)) {
    return 108;
  }

  if (aliases.some((alias) => alias.length >= 4 && alias.includes(normalizedInput) && inputTokens.length >= 2)) {
    return 95;
  }

  const bestTokenRatio = aliases.reduce((best, alias) => {
    const aliasTokens = new Set(alias.split(' ').filter((token) => token.length >= 2));
    if (!aliasTokens.size) return best;

    const matched = inputTokens.filter((token) => aliasTokens.has(token)).length;
    return Math.max(best, matched / inputTokens.length);
  }, 0);

  if (bestTokenRatio === 1 && inputTokens.length >= 3) return 85;
  if (bestTokenRatio >= 0.75 && inputTokens.length >= 3) return 70;

  return 0;
}

export function findMatchingSellerOption(rawValue: unknown, options: SellerOptionLike[] = []): SellerOptionLike | null {
  const value = String(rawValue ?? '').trim();
  if (!value || options.length === 0) return null;

  const directMatch = options.find((option) =>
    String(option.value) === value ||
    String(option.label) === value ||
    getSellerOptionDisplayValue(option) === value
  );
  if (directMatch) return directMatch;

  const scoredMatches = options
    .map((option) => ({ option, score: getSellerOptionMatchScore(value, option) }))
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score);

  if (!scoredMatches.length) return null;
  if (scoredMatches[0].score < 95) return null;
  if (scoredMatches[0].score === (scoredMatches[1]?.score ?? 0)) return null;

  return scoredMatches[0].option;
}

export function normalizeSellerValue(rawValue: unknown, options: SellerOptionLike[] = []): string {
  const value = String(rawValue ?? '').trim();
  if (!value) return '';

  const option = findMatchingSellerOption(value, options);
  if (option) return getSellerOptionDisplayValue(option);

  return stripSellerIdPrefix(value);
}

export function normalizeSellerValues(
  rawValues: string[],
  options: SellerOptionLike[] = [],
  excludedValues: string[] = []
): string[] {
  const excluded = new Set(excludedValues.map((value) => normalizeSellerText(normalizeSellerValue(value, options))));

  return Array.from(new Set(
    rawValues
      .map((value) => normalizeSellerValue(value, options))
      .filter(Boolean)
      .filter((value) => !excluded.has(normalizeSellerText(value)))
  ));
}

export function isSellerOptionExcluded(option: SellerOptionLike, excludedValues: string[]): boolean {
  if (excludedValues.length === 0) return false;

  const normalizedExcluded = new Set(excludedValues.map((value) => normalizeSellerText(normalizeSellerValue(value, [option]))));
  return getSellerOptionAliases(option).some((alias) => normalizedExcluded.has(alias));
}

export function formatSellerValuesForExport(value: string | string[] | undefined): string {
  const values = Array.isArray(value)
    ? value
    : String(value ?? '').split(',');

  return values
    .map((item) => stripSellerIdPrefix(item))
    .map((item) => item.trim())
    .filter(Boolean)
    .join(',');
}

export interface SellerFieldLike {
  key: string;
  options?: SellerOptionLike[];
}

export interface SellerSectionLike {
  fields?: SellerFieldLike[];
}

export interface SellerTemplateLike {
  sections?: SellerSectionLike[];
}

export function getSellerOptionsFromTemplate(template?: SellerTemplateLike | null): SellerOptionLike[] {
  const fields = template?.sections?.flatMap((section) => section.fields ?? []) ?? [];
  const sellerField = fields.find((field) => isSellerFieldKey(field.key) && field.options?.length);

  return sellerField?.options ?? [];
}

export function normalizeSellerFieldsInValues<T extends Record<string, string | string[]>>(
  values: T,
  options: SellerOptionLike[] = []
): T {
  const next = { ...values };

  Object.entries(next).forEach(([key, value]) => {
    if (!isSellerFieldKey(key)) return;

    const rawValues = Array.isArray(value)
      ? value.map((item) => String(item))
      : String(value ?? '').split(',');

    next[key as keyof T] = normalizeSellerValues(rawValues, options) as T[keyof T];
  });

  return next;
}
